import {FormControl, FormField, IconButton} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';

import {UseFormReturn, useFieldArray} from 'react-hook-form';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {DAY_LABEL_WIDTH} from '../constants/DAY_LABEL_WIDTH';
import {TIME_INPUT_WIDTH} from '../constants/TIME_INPUT_WIDTH';
import {
  BusinessHoursDay,
  EMPTY_HOURS_SLOT,
  TelephonyLineFormValues,
} from '../utils/telephonyLineForm';

interface TelephonyLineBusinessHoursDayRowProps extends RequiredTestIdProps {
  day: BusinessHoursDay;
  control: FormControl<TelephonyLineFormValues>;
  formApi: UseFormReturn<TelephonyLineFormValues>;
  isReadOnly: boolean;
}

/**
 * One weekday of the schedule: a stack of `from`–`to` slots so a lunch break or a split shift
 * survives the round trip instead of being truncated to the first range.
 */
export function TelephonyLineBusinessHoursDayRow(props: TelephonyLineBusinessHoursDayRowProps) {
  const {fields, append, remove} = useFieldArray({
    control: props.control,
    name: `businessHours.${props.day}`,
  });

  const revalidateSlot = (index: number) => () => {
    if (props.formApi.formState.isSubmitted) {
      props.formApi.trigger([
        `businessHours.${props.day}.${index}.from`,
        `businessHours.${props.day}.${index}.to`,
      ]);
    }
  };

  return (
    <VStack spacing={2}>
      {fields.map((field, index) => (
        <HStack key={field.id} spacing={4} align="flex-start">
          <Box width={DAY_LABEL_WIDTH} paddingTop={2}>
            <Show when={index === 0}>
              <Text size="base">{i18n.t(`general.labels.calendar.${props.day}`)}</Text>
            </Show>
          </Box>
          <Box width={TIME_INPUT_WIDTH}>
            <FormField
              control={props.control}
              type="time"
              name={`businessHours.${props.day}.${index}.from`}
              placeholder="00:00"
              isDisabled={props.isReadOnly}
              onChange={revalidateSlot(index)}
              data-testid={suffixTestId(`${props.day}-${index}-from`, props)}
            />
          </Box>
          <Box width={TIME_INPUT_WIDTH}>
            <FormField
              control={props.control}
              type="time"
              name={`businessHours.${props.day}.${index}.to`}
              placeholder="00:00"
              isDisabled={props.isReadOnly}
              onChange={revalidateSlot(index)}
              data-testid={suffixTestId(`${props.day}-${index}-to`, props)}
            />
          </Box>
          <Show when={!props.isReadOnly}>
            <HStack spacing={1}>
              <Show when={fields.length > 1}>
                <IconButton
                  icon="action/delete"
                  size="small"
                  onClick={() => remove(index)}
                  aria-label={i18n.t('general.actions.remove')}
                  data-testid={suffixTestId(`${props.day}-${index}-remove`, props)}
                />
              </Show>
              <Show when={index === fields.length - 1}>
                <IconButton
                  icon="content/add"
                  size="small"
                  onClick={() => append(EMPTY_HOURS_SLOT)}
                  aria-label={i18n.t('general.actions.add')}
                  data-testid={suffixTestId(`${props.day}-add`, props)}
                />
              </Show>
            </HStack>
          </Show>
        </HStack>
      ))}
    </VStack>
  );
}
